import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { object, string } from 'yup';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { PATH_RESET_PASSWORD, PATH_LOGIN } from '@jwp/ott-common/src/paths';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { useOtpInput } from '@jwp/ott-hooks-react/src/useOtpInput';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import * as CustomAccountService from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import AuthLayout from '../../containers/AuthLayout/AuthLayout';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';

import EmailVerificationForm from './components/EmailVerificationForm';
import OTPVerificationForm from './components/OTPVerificationForm';
import styles from './ForgotPassword.module.scss';

const ForgotPassword = () => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const announce = useAriaAnnouncer();
  const { siteName, authConfig } = useConfigStore((s) => ({ siteName: s.config.siteName, authConfig: s.featureFlags?.authentication }));
  const emailRegex = React.useMemo(() => {
    try {
      return authConfig?.email_regex ? new RegExp(authConfig.email_regex) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    } catch (e) {
      console.error('Invalid email regex from config:', e);
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    }

  }, [authConfig?.email_regex]);

  const {
    otp,
    inputRefs,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    handleFocus,
    otpValue,
    clear,
  } = useOtpInput(4);

  const [isSentOTP, setIsSentOTP] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [timer, setTimer] = useState(0);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [emailError, setEmailError] = useState("");
  
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const [requestOTPLoading, setRequestOTPLoading] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [requestOTPData, setRequestOTPData] = useState<any>(null);

  const { values, errors, submitting, handleSubmit, handleChange } = useForm<{ email: string }>({
    initialValues: { email: '' },
    silentValidation: true,
    validationSchema: object().shape({
      email: string()
        .trim()
        .required(t('login.field_required', { field: t('login.email') }))
        .matches(emailRegex, t('login.field_is_not_valid_email')),
    }),

    onSubmit: async ({ email }) => {
      clear();
      setRequestOTPLoading(true);
      try {
        const response: any = await CustomAccountService.requestForgetPasswordOtp(email);
        setRequestOTPData(response.data);
        
        if (response?.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }

        if (response?.data) {
          const type = response?.data?.requestForgetPasswordOtp?.type;
          if (type === "otp") {
            setIsSentOTP(true);
            setExpiryDate(new Date(response?.data?.requestForgetPasswordOtp?.expiresAt));
          } else {
            const message = response?.data?.requestForgetPasswordOtp?.message || t('reset.reset_link_sent');
            navigate(modalURLFromLocation(location, 'reset-password-success-play', { message }));
          }
        }
      } catch (err: any) {
        console.error(err);
        throw err;
      } finally {
        setRequestOTPLoading(false);
      }
    },
    onSubmitError: ({ error }) => {
      let errorMessage = t('login.unexpected_error');
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      showToast(errorMessage, 'error');
    },
  });

  const handleRecoveryByUsername = async () => {
    const email = values.email.trim();
    if (!email || !emailRegex.test(email)) {
      setEmailError(t('login.field_is_not_valid_email'));
      return;
    }

    setRecoveryLoading(true);
    try {
      const response: any = await CustomAccountService.sendUserRecoveryMail(email);

      if (response?.errors && response.errors.length > 0) {
        showToast(response.error.message, 'error');
        return;
      }

      if (response?.data) {
        navigate(modalURLFromLocation(location, 'recovery-username'));
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || t('reset.invalid_otp'), 'error');
    } finally {
      setRecoveryLoading(false);
      setEmailError("");
    }
  };


  const handleOTPVerification = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (otpValue.length < 4) {
      setOtpError(true);
      return;
    }
    setOtpVerifyLoading(true);
    try {
      setOtpError(false);
      const emailId = requestOTPData?.requestForgetPasswordOtp?.emailId || values.email;
      const response = await CustomAccountService.validateOtp(emailId, otpValue);
      if (response?.data) {
        sessionStorage.setItem("fp_email", emailId);
        sessionStorage.setItem("fp_code", otpValue);
        navigate(PATH_RESET_PASSWORD);
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || t('reset.invalid_otp'), 'error');

    } finally {
      setOtpVerifyLoading(false);
    }
  };

  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendAction = async () => {
    setResendError(null);
    setRequestOTPLoading(true);
    try {
      const emailId = requestOTPData?.requestForgetPasswordOtp?.emailId || values.email;
      const response: any = await CustomAccountService.requestForgetPasswordOtp(emailId);
      setRequestOTPData(response.data);
      
      if (response?.errors && response.errors.length > 0) {
        setResendError(response.errors[0].message);
        return;
      }

      if (response?.data) {
        const type = response?.data?.requestForgetPasswordOtp?.type;
        if (type === 'otp') {
          setExpiryDate(new Date(response?.data?.requestForgetPasswordOtp?.expiresAt));
          showToast(t('reset.otp_sent_to', { email: emailId }), 'success');
        } else {
          const message = response?.data?.requestForgetPasswordOtp?.message || t('reset.reset_link_sent');
          navigate(modalURLFromLocation(location, 'reset-password-success-play', { message }));
        }
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || 'Failed to resend code', 'error');
      setResendError(err.message || "Failed to resend code");
    } finally {
      setRequestOTPLoading(false);
    }
  };

  useEffect(() => {
    if (isSentOTP && expiryDate) {
      const calculateSecondsLeft = () => {
        const now = Date.now();
        const expiryTimestamp = expiryDate.getTime();
        const difference = expiryTimestamp - now;
        const remainingSeconds = Math.max(0, Math.floor(difference / 1000));
        setTimer(remainingSeconds);
        return remainingSeconds;
      };

      calculateSecondsLeft();
      const interval = setInterval(() => {
        if (calculateSecondsLeft() === 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isSentOTP, expiryDate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const currentEmail = requestOTPData?.requestForgetPasswordOtp?.emailId || values.email;

  return (
    <AuthLayout 
      title={isSentOTP ? t('reset.verify_otp') : t('reset.forgot_password_username')}
      subtitle={isSentOTP ? '' : t('reset.enter_email_assist')}
    >

      <Helmet>
        <title>{t('reset.forgot_password')} | {siteName}</title>
      </Helmet>
      
      <div className={styles.overlayWrap}>
        {submitting || otpVerifyLoading || requestOTPLoading ? (
          <div className={styles.loaderLayer} aria-live="polite" aria-busy>
            <LoadingOverlay transparentBackground inline />
          </div>
        ) : null}
        
        {isSentOTP ? (
          <OTPVerificationForm
            email={currentEmail}
            otp={otp}
            inputRefs={inputRefs}
            otpValue={otpValue}
            otpError={otpError}
            otpVerifyLoading={otpVerifyLoading}
            timer={timer}
            formatTime={formatTime}
            handleInputChange={handleInputChange}
            handleKeyDown={handleKeyDown}
            handlePaste={handlePaste}
            handleFocus={handleFocus}
            handleOTPVerification={handleOTPVerification}
            handleResendOTP={handleResendAction}
            resendError={resendError}
          />
        ) : (
          <EmailVerificationForm
            values={values}
            errors={errors}
            emailError={emailError}
            submitting={submitting}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            handleRecoveryByUsername={handleRecoveryByUsername}
            recoveryLoading={recoveryLoading}
          />
        )}
        <p className={styles.footer}>
          {t('reset.remember_password')}{' '}
          <Link className={styles.signUpLink} to={PATH_LOGIN}>
            {t('reset.sign_in')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
